// One-line previews of message elements.
//
// Two places need exactly the same sentence and must not drift: the threads
// list and the push notification body composed in the service worker. The
// translation function is a parameter because the two callers live in different
// worlds — the UI has the reactive i18n runtime, the worker only the plain
// dictionaries.

import type { Dict } from '$lib/i18n/en';
import type { MessageElement } from '$lib/protocol/types';

export type Translate = (key: keyof Dict, params?: Record<string, string | number>) => string;

export function elementPreview(t: Translate, element: MessageElement): string {
  switch (element.type) {
    case 'text':
      return element.text;
    case 'voice':
      return '🎤';
    case 'image':
      return `📷${element.caption !== undefined ? ` ${element.caption}` : ''}`;
    case 'question':
      return element.text;
    case 'answer':
      return element.text;
    case 'cart':
      return `🛒 ${t('el.cart.title')}`;
    case 'order':
      return `📦 ${t('el.order.title')}`;
    case 'notification':
      return element.text;
    case 'payment_request':
      return t('el.payment.title');
    case 'location':
      return `📍 ${element.name ?? element.address ?? ''}`;
    case 'calendar_event':
      return `📅 ${element.title}`;
    case 'video':
      return '🎬';
    case 'html':
      return element.fallbackText ?? '▤';
    default:
      return element.fallbackText ?? t('el.unknown');
  }
}

/** Wording for a preview that carries only an element TYPE — the shape a wake
 *  preview takes for everything except text (the localization rule of the
 *  WakePreview schema: the server does not know the device language, so
 *  structured types travel as types and the wording is composed here). */
export function typePreview(t: Translate, type: string, category?: string): string {
  switch (type) {
    case 'voice':
      return '🎤';
    case 'image':
      return '📷';
    case 'video':
      return '🎬';
    case 'location':
      return '📍';
    case 'cart':
      return `🛒 ${t('el.cart.title')}`;
    case 'order':
      return `📦 ${t('el.order.title')}`;
    case 'calendar_event':
      return '📅';
    case 'payment_request':
      return t('el.payment.title');
    case 'notification':
      switch (category) {
        case 'order_status':
          return t('contact.cat.order_status');
        case 'reminder':
          return t('contact.cat.reminder');
        case 'outage':
          return t('contact.cat.outage');
        case 'other':
          return t('contact.cat.other');
        default:
          return t('push.generic.body');
      }
    default:
      return t('push.generic.body');
  }
}
